import React, { useState } from 'react';

const RollbackModal = ({ 
  show, 
  onClose, 
  academicYearId, 
  academicYears, 
  onRollbackAcademicYear, 
  onRollbackSemester,
  onRollbackSpecificSemester,
  loading 
}) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [reason, setReason] = useState('');
  const [targetStatus, setTargetStatus] = useState('active');

  const academicYear = academicYears.find(ay => (ay.id || ay._id) === academicYearId);
  
  if (!show || !academicYear) return null;

  const isSemesterBased = academicYear.isSemesterBased;
  const completedSemesters = academicYear.semesters?.filter(s => s.status === 'completed') || [];

  const handleRollback = async () => {
    if (!selectedOption) return;

    try {
      if (selectedOption === 'academicYear') {
        await onRollbackAcademicYear(academicYearId, targetStatus, reason);
      } 
      else if (selectedOption.startsWith('semesterType-')) {
        const semesterType = selectedOption.replace('semesterType-', '');
        await onRollbackSemester(academicYearId, semesterType, targetStatus, reason);
      }
      else if (selectedOption.startsWith('semester-')) {
        const semesterNumber = parseInt(selectedOption.replace('semester-', ''));
        await onRollbackSpecificSemester(academicYearId, semesterNumber, targetStatus, reason);
      }
      
      onClose();
    } catch (error) {
      console.error('Rollback failed:', error);
    }
  };

  const getRollbackOptions = () => {
    const options = [];

    // Academic Year Rollback Option (always show if completed/inactive)
    if (academicYear.status === 'completed' || academicYear.status === 'inactive') {
      options.push({
        value: 'academicYear',
        label: `Rollback Entire Academic Year to ${targetStatus.toUpperCase()}`,
        description: `Set the entire academic year status to ${targetStatus}`,
        type: 'academicYear'
      });
    }

    // Semester Type Rollback Options
    if (isSemesterBased) {
      const oddCompleted = completedSemesters.filter(s => s.semesterType === 'odd').length > 0;
      const evenCompleted = completedSemesters.filter(s => s.semesterType === 'even').length > 0;

      if (oddCompleted) {
        options.push({
          value: 'semesterType-odd',
          label: 'Rollback All Odd Semesters',
          description: 'Rollback all completed odd semesters (1st, 3rd, 5th...)',
          type: 'semesterType'
        });
      }

      if (evenCompleted) {
        options.push({
          value: 'semesterType-even',
          label: 'Rollback All Even Semesters',
          description: 'Rollback all completed even semesters (2nd, 4th, 6th...)',
          type: 'semesterType'
        });
      }

      // Individual Semester Rollback Options
      completedSemesters.forEach(semester => {
        options.push({
          value: `semester-${semester.semesterNumber}`,
          label: `Rollback ${semester.semesterName}`,
          description: `Rollback only ${semester.semesterName} (${semester.semesterType})`,
          type: 'specificSemester',
          semesterNumber: semester.semesterNumber
        });
      });
    }

    return options;
  };

  const rollbackOptions = getRollbackOptions();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full mx-4 border border-blue-200">
        <div className="p-8">
          <div className="flex items-start mb-6">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
            </div>
            <div className="ml-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Rollback Options</h3>
              <p className="text-gray-600">
                Select what you want to rollback for <strong>{academicYear.name}</strong>
              </p>
              <div className="mt-2 flex items-center gap-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  academicYear.status === 'completed' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  Status: {academicYear.status}
                </span>
                {isSemesterBased && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                    Semester-based
                  </span>
                )}
              </div>
            </div>
          </div>

          {rollbackOptions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">No rollback options available</p>
              <p className="text-gray-500 text-sm mt-1">
                There are no completed items that can be rolled back.
              </p>
            </div>
          ) : (
            <>
              {/* Target Status Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rollback to Status:
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="active"
                      checked={targetStatus === 'active'}
                      onChange={(e) => setTargetStatus(e.target.value)}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="inactive"
                      checked={targetStatus === 'inactive'}
                      onChange={(e) => setTargetStatus(e.target.value)}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
              </div>

              {/* Rollback Options */}
              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                {rollbackOptions.map((option) => (
                  <label 
                    key={option.value}
                    className={`flex items-start p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      selectedOption === option.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      value={option.value}
                      checked={selectedOption === option.value}
                      onChange={(e) => setSelectedOption(e.target.value)}
                      className="mt-1 mr-3 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{option.label}</div>
                      <div className="text-sm text-gray-600 mt-1">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Reason Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rollback (Optional):
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why you're rolling back..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows="3"
                />
              </div>
            </>
          )}

          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Cancel
            </button>
            {rollbackOptions.length > 0 && (
              <button
                onClick={handleRollback}
                disabled={!selectedOption || loading}
                className={`flex-1 px-6 py-3 rounded-xl transition-all duration-200 font-semibold ${
                  !selectedOption || loading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {loading ? 'Processing...' : 'Confirm Rollback'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RollbackModal;